import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form, Formik } from 'formik';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';

import AcceptAgreementField from '../shared/AcceptAgreementField';
import Button from '../../../../shared/_TBComponents/Button';
import CommonFields from '../shared/CommonFields';
import GoogleAuthSection from '../shared/GoogleAuthSection';
import RegistrationFormWrapper from '../shared/RegistrationFormWrapper';
import { RegistrationSchema, RegistrationSchemaGoogle } from '../../../../shared/utils/registrationSchemaHelper';
import {
  cancelRegistration,
  registerUser,
} from '../../ducks/registrationActions';

const FormGroup = styled('div')`
  z-index: 1;
  
  label {
    width: 100%;
  }

  @media screen and (max-width: 425px) {
    margin-bottom: 10px;
  }
`;

const EasyJoinRegistrationForm = () => {
  const easyJoinData = useSelector((state) => state.registration.data.easyJoinData);
  const googleUser = useSelector((state) => state.registration.data.googleUser);
  const isRegisteringWithGoogle = useSelector(
    (state) => state.registration.data.isRegisteringWithGoogle,
  );
  const isSubmitting = useSelector((state) => state.registration.ui.isSubmitting);

  const dispatch = useDispatch();
  const theme = useTheme();

  const [googleResponse, setGoogleResponse] = useState(null);

  const handleCancel = () => {
    dispatch(cancelRegistration(false));
  };

  const handleGoogleConnect = (response) => {
    setGoogleResponse(response);
  };

  const handleRegistration = (values) => {
    let submissionObject = {};

    if (isRegisteringWithGoogle) {
      submissionObject = {
        acceptAgreement: values.acceptAgreement,
        authenticationMethod: 'GOOGLE',
        client_id: process.env.CLIENT_ID,
        client_secret: process.env.CLIENT_SECRET,
        countryCode: values.countryCode,
        easyJoinId: easyJoinData.easyJoinId,
        emailAddress: values.emailAddress,
        firstName: values.firstName,
        gender: values.gender,
        googleToken: googleResponse.tokenId,
        lastName: values.lastName,
        mobileNumber: values.phone,
        registrationType: 'EXISTING_ORGANIZATION',
      };
    } else {
      submissionObject = {
        acceptAgreement: values.acceptAgreement,
        authenticationMethod: 'PASSWORD',
        client_id: process.env.CLIENT_ID,
        client_secret: process.env.CLIENT_SECRET,
        countryCode: values.countryCode,
        easyJoinId: easyJoinData.easyJoinId,
        emailAddress: values.emailAddress,
        firstName: values.firstName,
        gender: values.gender,
        lastName: values.lastName,
        password: values.password,
        mobileNumber: values.phone,
        registrationType: 'EXISTING_ORGANIZATION',
      };
    }

    dispatch(registerUser(submissionObject));
  };

  return (
    <Formik
      enableReinitialize
      initialValues={{
        acceptAgreement: false,
        countryCode: undefined,
        emailAddress: googleUser.email || '',
        easyJoinId: easyJoinData.easyJoinId,
        firstName: googleUser.givenName || '',
        gender: undefined,
        lastName: googleUser.familyName || '',
        password: '',
        phone: undefined,
      }}
      onSubmit={(values) => {
        handleRegistration(values);
      }}
      validationSchema={isRegisteringWithGoogle ? RegistrationSchemaGoogle : RegistrationSchema}
      render={({
        errors,
        touched,
        handleSubmit,
        setFieldValue,
        values,
      }) => (
        <RegistrationFormWrapper
          subtitle='Enter the necessary details below to create your new account.'
          title='Registration'
        >
          <Form
            id='registration'
            name='registration'
          >
            <GoogleAuthSection
              onGoogleConnect={handleGoogleConnect}
            />
            <CommonFields
              errors={errors}
              setFieldValue={setFieldValue}
              showPasswordFields={!isRegisteringWithGoogle}
              touched={touched}
              values={values}
            />
            <AcceptAgreementField
              error={errors.acceptAgreement}
              setFieldValue={setFieldValue}
              touched={touched.acceptAgreement}
              values={{
                acceptAgreement: values.acceptAgreement,
              }}
            />
            <FormGroup>
              <Button
                bgColor={theme.colors.orange}
                disabled={
                  errors.password
                    || errors.emailAddress
                    || errors.confirmedPassword
                    || errors.acceptAgreement
                    || errors.phone
                    || errors.countryCode
                    || isSubmitting
                    || !values.acceptAgreement
                    || !values.emailAddress?.trim()
                    || !values.firstName?.trim()
                    || !values.lastName?.trim()
                    || (
                      !isRegisteringWithGoogle
                      && (!values.password?.trim() || !values.confirmedPassword?.trim())
                    )
                }
                fontSize='14px'
                fullWidth
                id='submit'
                large
                name='submit'
                noBorder
                onClick={handleSubmit}
                rounded
                txtColor={theme.colors.white}
                type='submit'
                upperCase
              />
              <Button
                fontSize='14px'
                fullWidth
                large
                name='Return to login'
                noBorder
                onClick={handleCancel}
                styledWrapperProps={{ marginTop: '15px' }}
                txtColor={theme.colors.lightText[1]}
                type='button'
                upperCase
              />
            </FormGroup>
          </Form>
        </RegistrationFormWrapper>
      )}
    />
  );
};

export default EasyJoinRegistrationForm;
